import React, { useEffect } from "react";

const GOOGLE_TRANSLATE_ELEMENT_ID = "google_translate_element";
const GOOGLE_TRANSLATE_SCRIPT_ID = "google-translate-script";
const GOOGLE_TRANSLATE_CALLBACK = "googleTranslateElementInit";
const INCLUDED_LANGUAGES = "ar,bn,bs,de,el,en,es,fa,fr,hi,hmn,hr,ht,it,iw,ja,ko,my,pl,pt,ru,so,sq,sr,su,sw,th,tr,uk,ur,vi,zh-CN";

let googleTranslateScriptPromise;

const resetGoogleTranslateScript = () => {
  document.getElementById(GOOGLE_TRANSLATE_SCRIPT_ID)?.remove();
  googleTranslateScriptPromise = null;

  if (window.google?.translate) {
    delete window.google.translate;
  }
};

const loadGoogleTranslateScript = ({ reload = false } = {}) => {
  if (reload) {
    resetGoogleTranslateScript();
  }

  if (window.google?.translate?.TranslateElement) {
    return Promise.resolve();
  }

  if (googleTranslateScriptPromise) {
    return googleTranslateScriptPromise;
  }

  googleTranslateScriptPromise = new Promise((resolve, reject) => {
    window[GOOGLE_TRANSLATE_CALLBACK] = resolve;

    const existingScript = document.getElementById(GOOGLE_TRANSLATE_SCRIPT_ID);

    if (existingScript) {
      existingScript.addEventListener("load", resolve, { once: true });
      existingScript.addEventListener("error", reject, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = GOOGLE_TRANSLATE_SCRIPT_ID;
    script.src = `https://translate.google.com/translate_a/element.js?cb=${GOOGLE_TRANSLATE_CALLBACK}`;
    if (reload) {
      script.src += `&reload=${Date.now()}`;
    }
    script.type = "text/javascript";
    script.async = true;
    script.onerror = reject;

    document.body.appendChild(script);
  });

  return googleTranslateScriptPromise;
};

const initializeGoogleTranslate = ({ reset = false } = {}) => {
  const translateElement = window.google?.translate?.TranslateElement;
  const container = document.getElementById(GOOGLE_TRANSLATE_ELEMENT_ID);

  if (!translateElement || !container) {
    return;
  }

  if (reset) {
    container.innerHTML = "";
  }

  if (container.querySelector(".goog-te-gadget-simple")) {
    return;
  }

  container.innerHTML = "";

  new translateElement(
    {
      pageLanguage: "en",
      includedLanguages: INCLUDED_LANGUAGES,
      layout: translateElement.InlineLayout.SIMPLE,
      gaTrack: true,
      gaId: "UA-215797-1",
    },
    GOOGLE_TRANSLATE_ELEMENT_ID
  );
};

// Patch aria-expanded on Google's injected trigger element.
// Google's widget renders an <a role="button" aria-haspopup="true"> but never sets
// aria-expanded. We can't replace it with a <button> without breaking the widget's
// own click handler, so we patch the attribute and observe the menu iframe lifecycle.
// Returns a cleanup function.
const patchGoogleTranslateAria = (container) => {
  const trigger = container.querySelector(".goog-te-gadget-simple a");
  if (!trigger) return () => {};

  trigger.setAttribute("aria-expanded", "false");

  let menuFrameObserver = null;

  function syncExpanded() {
    const frame = document.querySelector(".goog-te-menu-frame");
    const open = !!frame && frame.style.visibility !== "hidden" && frame.style.display !== "none";
    trigger.setAttribute("aria-expanded", String(open));
    // Once the frame exists, watch its style for show/hide toggling.
    if (frame && !menuFrameObserver) {
      menuFrameObserver = new MutationObserver(syncExpanded);
      menuFrameObserver.observe(frame, { attributes: true, attributeFilter: ["style"] });
    }
  }

  // Watch <body> for the menu iframe being added or removed.
  const bodyObserver = new MutationObserver((mutations) => {
    for (const { addedNodes, removedNodes } of mutations) {
      for (const node of addedNodes) {
        if (node.classList?.contains("goog-te-menu-frame")) syncExpanded();
      }
      for (const node of removedNodes) {
        if (node.classList?.contains("goog-te-menu-frame")) {
          trigger.setAttribute("aria-expanded", "false");
        }
      }
    }
  });

  bodyObserver.observe(document.body, { childList: true });

  return () => {
    bodyObserver.disconnect();
    menuFrameObserver?.disconnect();
  };
};

const loadAndInitializeGoogleTranslate = () => {
  return loadGoogleTranslateScript().then(initializeGoogleTranslate).catch(() => {});
};

const restoreGoogleTranslate = (event) => {
  return loadGoogleTranslateScript({ reload: event.persisted })
    .then(() => initializeGoogleTranslate({ reset: event.persisted }))
    .catch(() => {});
};

const GoogleTranslate = () => {
  useEffect(() => {
    let cleanupAria = () => {};

    loadAndInitializeGoogleTranslate().then(() => {
      const container = document.getElementById(GOOGLE_TRANSLATE_ELEMENT_ID);
      if (container) cleanupAria = patchGoogleTranslateAria(container);
    });

    window.addEventListener("pageshow", restoreGoogleTranslate);

    return () => {
      window.removeEventListener("pageshow", restoreGoogleTranslate);
      cleanupAria();
    };
  }, []);

  return <div id={GOOGLE_TRANSLATE_ELEMENT_ID}></div>;
};

export default GoogleTranslate;
